import { windowsToIana } from './timezone';

/**
 * Calendar-date formatting for the *facility's* day, not the browser's and
 * not UTC.
 *
 * The bug this replaces: `toLocalDateString()` converts to
 * UTC before the string is cut. Every US facility is behind UTC, so from
 * roughly 8pm Eastern onward "today" silently becomes tomorrow. In a 24-hour
 * kitchen that window covers dinner service and the whole night shift — tray
 * tickets printed at dinner carry the next day's menu, and evening weights
 * land on the wrong day, which feeds the weight-loss trigger reports.
 *
 * Browser-local formatting fixes that for anyone sitting in the building, but
 * not for a remote user: a regional manager in New York opening a California
 * facility at 11pm ET is looking at a kitchen where it is still 8pm and still
 * yesterday's service. `tblFacility.TimeZone_ID` exists precisely for this, so
 * these helpers resolve the facility's zone and fall back to browser-local
 * only when it is unset or outside the Windows→IANA map.
 *
 * The same trap applies in reverse to a Date already representing a local
 * calendar day (`new Date(2026, 7, 22)` — local midnight): converting that to
 * UTC in a negative-offset zone moves it to the *previous* day. Both shapes
 * are wrong, in opposite directions.
 */

/**
 * `YYYY-MM-DD` for a date as seen in `ianaZone`.
 * `en-CA` is used because its short date format is already ISO-ordered.
 */
function formatInZone(date: Date, ianaZone: string | null): string {
  if (!ianaZone) {
    // Browser-local, read from components so UTC is never involved.
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: ianaZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
}

/**
 * `YYYY-MM-DD` in the facility's zone.
 *
 * `facilityWindowsTz` is `tblFacility.TimeZone_ID` (a Windows ID such as
 * "Eastern Standard Time"). Pass `undefined` to use browser-local.
 */
export function toFacilityDateString(
  date: Date = new Date(),
  facilityWindowsTz?: string | null,
): string {
  return formatInZone(date, windowsToIana(facilityWindowsTz));
}

/** Today's calendar date in the facility's zone, as `YYYY-MM-DD`. */
export function todayFacilityDateString(facilityWindowsTz?: string | null): string {
  return toFacilityDateString(new Date(), facilityWindowsTz);
}

/**
 * `YYYY-MM-DDT00:00:00` — the wire shape the API expects for a date-only
 * value, matching Blazor's `DateTime.Now` serialization.
 */
export function toFacilityIsoDateTime(
  date: Date = new Date(),
  facilityWindowsTz?: string | null,
): string {
  return `${toFacilityDateString(date, facilityWindowsTz)}T00:00:00`;
}

/**
 * Browser-local `YYYY-MM-DD`, for the cases where the facility zone genuinely
 * does not apply — e.g. formatting a date the user just picked in their own
 * calendar widget, where re-interpreting it in another zone would shift it.
 */
export function toLocalDateString(date: Date = new Date()): string {
  return formatInZone(date, null);
}

/**
 * Browser-local `YYYY-MM-DDT00:00:00`.
 *
 * Used by the bulk migration off `toISOString().split('T')[0]`. It fixes the
 * UTC rollover — which is the operational bug, since the people printing tray
 * tickets and entering weights at 9pm are standing in the building — but it
 * still reports the viewer's day rather than the facility's. Paths where a
 * remote user's day could differ should move to `useFacilityDate()`.
 */
export function toLocalIsoDateTime(date: Date = new Date()): string {
  return `${toLocalDateString(date)}T00:00:00`;
}
